<template>
  <div>
    <!-- Loading and Error States -->
    <div v-if="isLoading" class="text-center my-3">Lade Transaktionen …</div>
    <div v-else-if="loadError" class="text-danger text-center my-3">{{ loadError }}</div>

    <!-- Filter Row -->
    <div v-else class="mb-3 row m-b-md">
      <div class="col-md-6 d-flex align-items-center">
        <label class="mr-2 font-weight-bold" for="action-filter">Aktionstyp filtern:</label>
        <select
            id="action-filter"
            v-model="actionFilter"
            class="form-control w-100"
        >
          <option value="">Alle</option>
          <option
              v-for="type in uniqueActionTypes"
              :key="type"
              :value="type"
          >
            {{ getActionLabel(type) }}
          </option>
        </select>
      </div>
      <div class="col-md-6 d-flex align-items-center">
        <label class="mr-2 font-weight-bold" for="status-filter">Status filtern:</label>
        <select
            id="status-filter"
            v-model="statusFilter"
            class="form-control w-100"
        >
          <option value="">Alle</option>
          <option value="pending">Ausstehend</option>
          <option value="expired">Abgelaufen</option>
        </select>
      </div>
    </div>

    <!-- Transactions Table -->
    <table v-if="!isLoading && !loadError" class="table table-striped">
      <thead>
      <tr>
        <th class="headerId">ID</th>
        <th>
          <button
              :aria-sort="getAriaSort('action_type')"
              class="btn btn-link p-0 font-weight-bold"
              role="columnheader"
              @click="sortByColumn('action_type')"
          >
            Aktionstyp
            <i :class="getSortIcon('action_type')" aria-hidden="true"></i>
          </button>
        </th>
        <th>
          <button
              :aria-sort="getAriaSort('booked_at')"
              class="btn btn-link p-0 font-weight-bold"
              role="columnheader"
              @click="sortByColumn('booked_at')"
          >
            Punkte gebucht am
            <i :class="getSortIcon('booked_at')" aria-hidden="true"></i>
          </button>
        </th>
        <th>
          <button
              :aria-sort="getAriaSort('expires_at')"
              class="btn btn-link p-0 font-weight-bold"
              role="columnheader"
              @click="sortByColumn('expires_at')"
          >
            Läuft ab am
            <i :class="getSortIcon('expires_at')" aria-hidden="true"></i>
          </button>
        </th>
        <th>Status</th>
        <th>Anmerkung</th>
        <th class="headerTextRight">Punkteänderung</th>
        <th class="headerTextRight">Statusänderung</th>
      </tr>
      </thead>
      <tbody>
      <!-- No-data row -->
      <tr v-if="!filteredTransactions.length">
        <td class="text-center" colspan="8">Keine Transaktionen verfügbar</td>
      </tr>

      <!-- Data rows -->
      <tr
          v-for="transaction in paginatedTransactions"
          :key="transaction.id"
      >
        <td class="headerId">{{ transaction.id }}</td>
        <td class="d-flex align-items-center">
          <i
              :class="getActionIconClass(transaction.action_type)"
              aria-hidden="true"
              style="font-size: 1.5rem; margin-right: 8px;"
          ></i>
          <span>{{ getActionLabel(transaction.action_type) }}</span>
        </td>
        <td class="text-center">
          {{ getFormattedDateAndTime(transaction.booked_at) }}
        </td>
        <td class="text-center">
          {{
            transaction.expires_at
                ? getFormattedDate(transaction.expires_at)
                : '–'
          }}
        </td>
        <td class="text-center">
          <template v-if="isExpired(transaction)">
            <span class="badge badge-danger ml-2" style="font-size: 1.25rem; padding: 0.25em 0.5em;">
              Abgelaufen
            </span>
          </template>
          <template v-else-if="isPurchasePending(transaction)">
            <span class="badge badge-warning ml-2" style="font-size: 1.25rem; padding: 0.25em 0.5em;">
              Ausstehend
            </span>
          </template>
        </td>
        <td class="text-center">
          <span class="badge badge-success">{{ transaction.memo }}</span>
        </td>
        <td
            class="text-right"
            :class="{
              'text-success': transaction.points_change > 0,
              'text-danger': transaction.points_change < 0
            }"
        >
          {{
            transaction.points_change > 0
                ? '+' + transaction.points_change
                : transaction.points_change
          }}
        </td>
        <td
            class="text-right"
            :class="{
              'text-success': transaction.status_change > 0,
              'text-danger': transaction.status_change < 0
            }"
        >
          {{
            transaction.status_change > 0
                ? '+' + transaction.status_change
                : transaction.status_change
          }}
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Pagination Controls -->
    <div
        v-if="!isLoading && !loadError"
        class="pagination"
    >
      <button
          :disabled="currentPage <= 1 || totalPages === 0"
          class="btn btn-secondary"
          @click="changePage(currentPage - 1)"
      >
        <i aria-hidden="true" class="fa fa-angle-left"></i>&nbsp;Vorherige
      </button>
      <span>Seite {{ currentPage }} von {{ totalPages || 1 }}</span>
      <button
          :disabled="currentPage >= totalPages || totalPages === 0"
          class="btn btn-secondary"
          @click="changePage(currentPage + 1)"
      >
        Nächste&nbsp;<i aria-hidden="true" class="fa fa-angle-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "LoyaltyPointTransactionsTable",
  props: {
    loyaltyAccountId: {
      type: [String, Number],
      required: true
    },
    daysPending: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      pointTransactions: [],
      isLoading: false,
      loadError: null,
      currentPage: 1,
      perPage: 20,
      sortBy: 'booked_at',
      sortDir: 'desc',
      actionFilter: '',
      statusFilter: ''
    };
  },
  computed: {
    uniqueActionTypes() {
      return [
        ...new Set(this.pointTransactions.map(t => t.action_type))
      ];
    },
    filteredTransactions() {
      let result = this.pointTransactions;
      if (this.actionFilter) {
        result = result.filter(t => t.action_type === this.actionFilter);
      }
      if (this.statusFilter === 'pending') {
        result = result.filter(t => this.isPurchasePending(t));
      }
      if (this.statusFilter === 'expired') {
        result = result.filter(t => this.isExpired(t));
      }
      return result;
    },
    sortedTransactions() {
      return this.filteredTransactions.slice().sort((a, b) => {
        const modifier = this.sortDir === 'asc' ? 1 : -1;
        if (['booked_at', 'expires_at'].includes(this.sortBy)) {
          const dateA = a[this.sortBy] ? new Date(a[this.sortBy]) : null;
          const dateB = b[this.sortBy] ? new Date(b[this.sortBy]) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1 * modifier;
          if (!dateB) return -1 * modifier;
          const diff = dateA - dateB;
          if (diff !== 0) {
            return modifier * diff;
          }
          return modifier * (a.id - b.id);
        }
        const valA = a[this.sortBy];
        const valB = b[this.sortBy];
        const numA = Number(valA);
        const numB = Number(valB);
        if (!isNaN(numA) && !isNaN(numB)) {
          if (numA < numB) return -1 * modifier;
          if (numA > numB) return 1 * modifier;
          return 0;
        }
        if (valA < valB) return -1 * modifier;
        if (valA > valB) return 1 * modifier;
        return 0;
      });
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.sortedTransactions.slice(start, start + this.perPage);
    },
    totalPages() {
      return Math.ceil(this.filteredTransactions.length / this.perPage);
    },
  },
  methods: {
    fetchTransactions() {
      this.isLoading = true;
      this.loadError = null;
      axios
          .get(
              `/backend/loyalty-program/accounts/${this.loyaltyAccountId}/point-transactions/`
          )
          .then(response => {
            this.pointTransactions =
                response.data.pointTransactions || response.data || [];
            if (this.currentPage > this.totalPages) {
              this.currentPage = this.totalPages || 1;
            }
          })
          .catch(err => {
            console.error(err);
            this.loadError = 'Fehler beim Laden der Transaktionen';
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    sortByColumn(column) {
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortDir = 'asc';
      }
      this.currentPage = 1;
    },
    getSortIcon(column) {
      if (this.sortBy !== column) return 'fa fa-sort';
      return this.sortDir === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down';
    },
    getAriaSort(column) {
      if (this.sortBy !== column) return 'none';
      return this.sortDir === 'asc' ? 'ascending' : 'descending';
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    getActionLabel(type) {
      const cfg = window.PointTransactionActionTypeConfig || {labels: {}};
      return cfg.labels[type] || 'Unbekannt';
    },
    getActionIconClass(type) {
      const cfg = window.PointTransactionActionTypeConfig || {icons: {}};
      return cfg.icons[type] || 'fa fa-question-circle';
    },
    getFormattedDateAndTime(dateStr) {
      if (!dateStr) return '–';
      const date = new Date(dateStr);
      const pad = n => n.toString().padStart(2, '0');
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    getFormattedDate(dateStr) {
      if (!dateStr) return '–';
      const date = new Date(dateStr);
      const pad = n => n.toString().padStart(2, '0');
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      return `${year}-${month}-${day}`;
    },
    isPurchasePending(transaction) {
      // Only PURCHASE and BONUS can ever be pending
      if (![1, 4].includes(transaction.action_type)) {
        return false;
      }

      // BONUS reasons that bypass pending
      const bonusReasonsWithoutPending = [
        'level_upgrade',
        'registration',
        'activation',
      ];

      if (
          transaction.action_type === 4 &&
          transaction.meta_data &&
          bonusReasonsWithoutPending.includes(transaction.meta_data.reason)
      ) {
        return false;
      }

      if (!transaction.booked_at || this.daysPending <= 0) {
        return false;
      }

      const bookedDate = new Date(transaction.booked_at);
      const pendingMs = this.daysPending * 24 * 60 * 60 * 1000;
      const pendingDeadline = new Date(bookedDate.getTime() + pendingMs);

      return new Date() < pendingDeadline;
    },
    isExpired(transaction) {
      if (!transaction.expires_at) {
        return false;
      }
      const expDate = new Date(transaction.expires_at);
      if (isNaN(expDate.getTime())) {
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today > expDate;
    }
  },
  watch: {
    loyaltyAccountId(newId, oldId) {
      if (newId !== oldId) {
        this.currentPage = 1;
        this.sortBy = 'booked_at';
        this.sortDir = 'desc';
        this.actionFilter = '';
        this.statusFilter = '';
        this.fetchTransactions();
      }
    },
    actionFilter() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchTransactions();
  }
};
</script>

<style scoped>
.table {
  width: 100%;
  font-size: 1.5rem;
}

th {
  text-align: center;
}

.headerId {
  text-align: left !important;
}

.headerTextRight {
  text-align: right !important;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 15px;
  gap: 10px;
  font-size: 1.5rem;
}

.text-success {
  font-weight: bold;
  color: green;
  text-align: right;
}

.text-danger {
  font-weight: bold;
  color: red;
  text-align: right;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.font-weight-bold {
  font-weight: 700;
}

.form-control {
  font-size: 1rem;
}
</style>
